import { Progress } from "@/components/ui/progress";
import { SubscriptionData, CreditsData } from "@/helpers/subscription.helper";
import { CircleAlertIcon } from "lucide-react";
import { Spinner } from "../ui/spinner";
import { motion } from "framer-motion";

export type SubscriptionProgressProps = {
  className?: string;
  subscriptionData?: SubscriptionData;
  creditsData?: CreditsData;
  isLoading?: boolean;
  utilisationError: any;
};

function formatEndDate(date?: Date | string): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return "";
  // Example: Nov 23
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export const getColorClasses = (status: 'success' | 'error' | 'warning' | ''): { border: string; text: string, progressIndicatorColor: string, progressBgColor: string } => {

  if (status === 'error') {
    // expired -> red
    return { border: "border-error-red", text: "text-error-red", progressIndicatorColor: "bg-error-red", progressBgColor: "bg-semantic-error-100" };
  }
  if (status === 'warning') {
    // nearing expiry -> yellow
    return { border: "border-semantic-yellow-500", text: "text-semantic-yellow-500", progressIndicatorColor: "bg-semantic-yellow-500", progressBgColor: "bg-progress-bg-color" };
  }
  // good amount of time left -> green
  return { border: "border-text-success", text: "text-text-success", progressIndicatorColor: "bg-primary-500", progressBgColor: "bg-progress-bg-color" };
};

export function SubscriptionProgress({
  className = "",
  subscriptionData,
  creditsData,
  isLoading,
  utilisationError
}: SubscriptionProgressProps) {
  const safeTotal = creditsData?.progress?.credits || 0;
  const percent = creditsData?.progress?.percentageOfUsage || 0;
  const endLabel = formatEndDate(subscriptionData?.nextBillingDate);

  const { progressIndicatorColor, progressBgColor } = getColorClasses(creditsData?.progress?.state || '');
  

  return (
    isLoading ? 
      <div className="w-full py-2">
        <span className="text-xs font-medium text-text-high-emphasis">Credits Used</span>
        <div className="flex gap-2 justify-center text-semantic-error-500 w-full">
          <Spinner className="text-[24px]" />
        </div>
      </div>
      
    :
      creditsData?.progress
      ?
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className={`w-full space-y-2 ${className} my-2`}>
          {/* Header: main label and sublabel */}
          <div className="flex items-center my-1 gap-1">
            <span className="text-xs font-medium text-text-high-emphasis">Credits Used</span>
            <span className="text-xs italic text-muted-foreground">
              ({subscriptionData?.tag?.name} ends on {endLabel})
            </span>
          </div>

          {/* Progress with numeric score on the right */}
          <div className="flex items-center gap-1 px-1">
            <Progress value={percent} className={`h-[6px] flex-1 ${progressBgColor}`} indicatorClassName={progressIndicatorColor} />
            <span className={`min-w-[40px] text-right text-xs tabular-nums`}>
              {creditsData?.progress?.usedCredits}/{safeTotal}
            </span>
          </div>
          {
            creditsData?.progress?.usedCredits===safeTotal && <div className="text-xs italic text-text-error">Upgrade to add more credits</div>
          }
        </motion.div>
      :
        utilisationError ? 
        <div className="flex gap-2 align-center text-semantic-error-500">
          <CircleAlertIcon className="text-semantic-error-500" /><span>Credits data is unavailable</span>
        </div>
        : <></>
  );
}
